export type HomeAboutTextVariant = "default" | "accent" | "emphasis" | "italic" | "resource";

export type HomeAboutTextSegment = {
  text: string;
  variant?: HomeAboutTextVariant;
};

export type HomeAboutRichParagraph = HomeAboutTextSegment[];

/** Joins segment text for tests and plain-text fallbacks. */
export const flattenHomeAboutParagraph = (segments: HomeAboutRichParagraph): string =>
  segments.map((segment) => segment.text).join("");

/** Optional personal founder layer below the core HomeCook Story (see README for A/B/C variants). */
export type HomeAboutPersonalStory = {
  title?: string;
  paragraphs?: HomeAboutRichParagraph[];
  inlineParagraph?: HomeAboutRichParagraph;
};

/** Option B — full understated block (set as personalStory to enable). */
export const homeAboutPersonalStoryOptionB: HomeAboutPersonalStory = {
  title: "Why It Became Personal",
  paragraphs: [
    [
      { text: "For Hassan Badru, the disconnect was not abstract. Arriving in the U.S. as a student, later losing his father and the structural support that came with him, he experienced how hard it can be to find nourishing food, and how local food entrepreneurs stepped in." },
    ],
    [
      { text: "Working alongside Tayana Harrison in communities where fresh options were scarce, they saw the same pattern from the other side: people building food businesses without the infrastructure to sustain them." },
    ],
  ],
};

/** Option C — blend as a short paragraph before the belief (active default). */
export const homeAboutPersonalStoryOptionC: HomeAboutPersonalStory = {
  inlineParagraph: [
    { text: "For our founders, this work is " },
    { text: "personal", variant: "accent" },
    { text: ", rooted in " },
    { text: "food access", variant: "emphasis" },
    { text: ", community, and the entrepreneurs who showed up when it mattered." },
  ],
};

/** Shared Home landing page About copy — single source of truth for Desktop and Mobile. */
export const homeAboutContent = {
  eyebrow: "About Us",
  titleHighlight: "The",
  titleRest: "HomeCook Story",
  imageAlt: "HomeCook co-founders presenting to food entrepreneurs and community partners",
  paragraphs: [
    [
      { text: "Across the D.C. region, our founders kept seeing the same pattern: " },
      { text: "food entrepreneurs", variant: "accent" },
      { text: " ready to grow, but the " },
      { text: "culinary resources", variant: "accent" },
      { text: " they needed: " },
      { text: "kitchen space", variant: "resource" },
      { text: ", " },
      { text: "training", variant: "resource" },
      { text: ", " },
      { text: "documentation", variant: "resource" },
      { text: ", " },
      { text: "funding", variant: "resource" },
      { text: ", " },
      { text: "mentorship", variant: "resource" },
      { text: ", and " },
      { text: "local food sources", variant: "resource" },
      { text: ", were " },
      { text: "hard to reach", variant: "accent" },
      { text: ". " },
      // { text: "The ", variant: "" },
      { text: "The pandemic", variant: "emphasis" },
      { text: " made that gap impossible to ignore." },
    ],
    [
      { text: "Meanwhile," },
      { text: " kitchens ", variant: "emphasis" },
      { text: "in restaurants, schools, faith-based organizations, and community spaces often " },
      { text: "sat underused", variant: "accent" },
      { text: ". The problem was " },
      { text: "bigger than finding an open kitchen", variant: "emphasis" },
      { text: "; requirements, scheduling, payments, records, and coordination were scattered across disconnected tools." },
    ],
    [
      { text: "HomeCook is the " },
      { text: "operational infrastructure", variant: "accent" },
      { text: " that " },
      { text: "connects both sides", variant: "emphasis" },
      { text: ", helping entrepreneurs access culinary resources while giving providers better ways to activate capacity and manage the businesses they support." },
    ],
  ] as HomeAboutRichParagraph[],
  belief: {
    lead: "Our belief is simple:",
    body: [
      { text: "we don't always need more infrastructure, we need the " },
      { text: "infrastructure already in our communities", variant: "italic" },
      { text: " to work better for the people building what's next." },
    ] as HomeAboutRichParagraph,
  },
  personalStory: homeAboutPersonalStoryOptionC as HomeAboutPersonalStory | null,
};
